import React, { useState, useEffect } from "react";
import { Button, ColorPicker, Form, Alert, Typography, Switch, Select } from "antd";
import { toast } from "react-toastify";
import axiosAPI from "../../appRedux/services/axiosCommon";

const { Text } = Typography;

const GlobalSettingsForm = ({ onClose }) => {
  const [isEnabled, setIsEnabled] = useState(true);
  const [backgroundColor, setBackgroundColor] = useState("#0f172a");
  const [titleTextColor, setTitleTextColor] = useState("#fbbf24");
  const [descriptionTextColor, setDescriptionTextColor] = useState("#ffffff");
  const [ctaTextColor, setCtaTextColor] = useState("#ffffff");
  const [allowedPages, setAllowedPages] = useState(["/", "/course", "/deals"]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axiosAPI.get("/v1/global-settings/offer_strip");
        if (response.data && response.data.results) {
          setIsEnabled(response.data.results.isEnabled !== false);
          setBackgroundColor(response.data.results.backgroundColor || "#0f172a");
          setTitleTextColor(response.data.results.titleTextColor || response.data.results.textColor || "#fbbf24");
          setDescriptionTextColor(response.data.results.descriptionTextColor || response.data.results.textColor || "#ffffff");
          setCtaTextColor(response.data.results.ctaTextColor || response.data.results.descriptionTextColor || response.data.results.textColor || "#ffffff");
          
          if (response.data.results.allowedPages && Array.isArray(response.data.results.allowedPages)) {
            setAllowedPages(response.data.results.allowedPages);
          }
        }
      } catch (error) {
        console.error("Error fetching global settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setLoading(true);
    try {
      await axiosAPI.put("/v1/admin/global-settings", {
        key: "offer_strip",
        value: { isEnabled, backgroundColor, titleTextColor, descriptionTextColor, ctaTextColor, allowedPages },
      });
      toast.success("Global settings updated successfully");
      if (onClose) onClose();
    } catch (error) {
      toast.error("Failed to update global settings");
      console.error("Error saving global settings:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-2 py-3">
      <style>{`
        .ant-color-picker-dropdown, 
        .ant-popover {
          z-index: 99999 !important;
        }
      `}</style>
      <Alert
        message="Live Preview"
        description={
          <div 
            style={{
              background: backgroundColor,
              padding: "16px 20px",
              borderRadius: "8px",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "12px",
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
            }}
          >
            <span>🔥</span>
            <span style={{ color: titleTextColor, fontWeight: "bold" }}>Special Deal:</span>
            <span style={{ color: descriptionTextColor }}>Complete Your Course Today!</span>
            <span style={{ 
              color: ctaTextColor,
              borderColor: ctaTextColor,
              border: `1px solid ${ctaTextColor}`,
              background: 'rgba(255,255,255,0.1)', 
              padding: '4px 12px', 
              borderRadius: '4px',
              textDecoration: 'none',
              cursor: 'pointer'
            }}>
              CLAIM DEAL
            </span>
          </div>
        }
        type="info"
        showIcon
        className="mb-4 bg-light border-0"
      />

      <Form 
        form={form} 
        layout="vertical" 
        size="large"
      >
        <div className="mb-4 d-flex align-items-center gap-3 border-bottom pb-3">
          <Text strong className="mb-0">Enable Global Offer Strip</Text>
          <Switch 
            checked={isEnabled} 
            onChange={(checked) => setIsEnabled(checked)} 
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
        </div>

        <div className="row">
          <div className="col-md-6">
            <Form.Item label={<Text strong>Strip Background Color</Text>}>
              <ColorPicker
                value={backgroundColor}
                onChange={(color) => setBackgroundColor(color.toHexString())}
                showText
                format="hex"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          
          <div className="col-md-6">
            <Form.Item label={<Text strong>Title Text Color</Text>}>
              <ColorPicker
                value={titleTextColor}
                onChange={(color) => setTitleTextColor(color.toHexString())}
                showText
                format="hex"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <div className="col-md-6 mt-3">
            <Form.Item label={<Text strong>Description Text Color</Text>}>
              <ColorPicker
                value={descriptionTextColor}
                onChange={(color) => setDescriptionTextColor(color.toHexString())}
                showText
                format="hex"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>

          <div className="col-md-6 mt-3">
            <Form.Item label={<Text strong>Button Text Color</Text>}>
              <ColorPicker
                value={ctaTextColor}
                onChange={(color) => setCtaTextColor(color.toHexString())}
                showText
                format="hex"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          
          <div className="col-md-12 mt-3">
            <Form.Item 
              label={<Text strong>Allowed Pages</Text>}
              help="Type or select the page paths where this offer strip should appear (e.g. / for home, /course for listing). It will implicitly match any route that starts with the value."
            >
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Enter allowed paths (e.g., /, /course, /deals)"
                value={allowedPages}
                onChange={setAllowedPages}
                options={[
                  { value: '/', label: 'Home (/)' },
                  { value: '/course', label: 'Courses (/course)' },
                  { value: '/deals', label: 'Deals (/deals)' },
                  { value: '/skills', label: 'Skills (/skills)' },
                  { value: '/compare', label: 'Compare (/compare)' },
                ]}
              />
            </Form.Item>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
          {onClose && (
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
          )}
          <Button size="large" type="primary" onClick={handleSave} loading={loading}>
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default GlobalSettingsForm;
